import type { CataloguePlace, NearPlace } from '@/lib/placeCatalogue'

/**
 * The map Emma draws while she connects a place (Brian's onboarding v1.5 to v1.6): the neighbours at their real bearing
 * and distance, north up, the rings in miles, the outer ring the radius. One per compass direction first, so nothing
 * clumps; a label that would sit on another (or on the pin) steps away from the centre. lit: how many wires and dots
 * are lit so far (Emma lights them as she reads); labels: whether the names show yet; still: the sweep has stopped.
 */
export function PlaceRadar({ home, near, radius, lit, labels, still }: {
  home: CataloguePlace
  near: NearPlace[]
  radius: number | null
  lit: number
  labels: boolean
  still: boolean
}) {
  const W = 360, H = 224, cx = 180, cy = 112
  const far = radius || Math.max(10, ...near.map((x) => x.miles))
  const ringMi = [Math.round(far / 3), Math.round((far * 2) / 3), Math.round(far)]
  const pts = near.map((x) => {
    const a = ((x.deg - 90) * Math.PI) / 180, d = 0.25 + (0.75 * Math.min(x.miles, far)) / far
    return { x, px: cx + Math.cos(a) * 165 * d, py: cy + Math.sin(a) * 99 * d }
  })
  type Placed = (typeof pts)[number] & { ly: number; side: 'start' | 'end'; x0: number; x1: number; i: number }
  const placed: Placed[] = []
  pts.map((q, i) => ({ ...q, i }))
    .sort((a, b) => Math.hypot(a.px - cx, a.py - cy) - Math.hypot(b.px - cx, b.py - cy))
    .forEach((q) => {
      const side: 'start' | 'end' = q.px > W * 0.66 ? 'end' : q.px < W * 0.34 ? 'start' : q.px > cx ? 'start' : 'end'
      const up = q.py < cy
      const wpx = (q.x.name.length + 10) * 6.2
      let ly = up ? q.py - 7 : q.py + 14
      const x0 = side === 'start' ? q.px + 8 : q.px + 8 - wpx - 16, x1 = x0 + wpx
      const hits = (o: Placed) => Math.abs(o.ly - ly) < 13 && x0 < o.x1 && x1 > o.x0
      const pin = () => Math.abs(ly - (cy + 10)) < 24 && x0 < cx + 24 && x1 > cx - 24
      for (let k = 0; k < 8 && (placed.some(hits) || pin()); k++) ly += up ? -13 : 13
      placed.push({ ...q, ly, side, x0, x1 })
    })
  return (
    <div className={`ob-radar${still ? ' still' : ''}`}>
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <defs>
          <radialGradient id="obrg" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="rgba(232,152,56,.35)" /><stop offset="1" stopColor="rgba(232,152,56,0)" /></radialGradient>
          <linearGradient id="obsw" x1="0" x2="1"><stop offset="0" stopColor="rgba(232,152,56,0)" /><stop offset="1" stopColor="rgba(232,152,56,.35)" /></linearGradient>
        </defs>
        <ellipse cx={cx} cy={cy} rx="170" ry="104" fill="url(#obrg)" opacity=".5" />
        {[1, 2, 3].map((k) => <ellipse key={k} className="ring" cx={cx} cy={cy} rx={k * 55} ry={k * 33} />)}
        {near.length > 0 && ringMi.map((mi, k) => <text key={k} className="mi" x={cx + (k + 1) * 55 - 4} y={cy - 4} textAnchor="end">{mi} mi</text>)}
        {near.length > 0 && <text className="mi" x={cx} y={cy - 105} textAnchor="middle">N</text>}
        <path className="sweep" d={`M${cx} ${cy} L${cx + 165} ${cy - 30} A168 100 0 0 1 ${cx + 168} ${cy + 6} Z`} fill="url(#obsw)" />
        {pts.map(({ px, py }, i) => <line key={`w${i}`} className={`wire${i < lit ? ' in' : ''}`} x1={cx} y1={cy} x2={px.toFixed(1)} y2={py.toFixed(1)} />)}
        {pts.map(({ px, py }, i) => <circle key={`s${i}`} className={`src${i < lit ? ' in' : ''}`} cx={px.toFixed(1)} cy={py.toFixed(1)} r="4" fill="#c8854a" />)}
        {placed.map(({ x, px, py, ly, side, i }) => (
          <g key={`n${i}`} className={`nb${labels ? ' in' : ''}`}>
            <circle cx={px.toFixed(1)} cy={py.toFixed(1)} r="3.5" fill="none" stroke="#b0b0b8" strokeWidth="1.2" />
            <text x={(px + (side === 'end' ? -8 : 8)).toFixed(1)} y={ly.toFixed(1)} textAnchor={side}>
              {x.name}<tspan className="sub"> · {x.miles} mi {x.dir}{x.country && x.country !== home.country ? ` · ${x.country}` : ''}</tspan>
            </text>
          </g>
        ))}
        <g className="pin"><circle cx={cx} cy={cy} r="9" fill="#c8854a" /><circle cx={cx} cy={cy} r="3.5" fill="#1b130b" /></g>
        <text className="home" x={cx} y={cy + 28} textAnchor="middle">{home.name}</text>
      </svg>
    </div>
  )
}
